/**
 * TourContext — shared state between the tour hook and UI components.
 *
 * The tour hook sets signals (e.g. showDemoProperty) that UI components
 * listen for during the tour.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

// =============================================================================
// Types
// =============================================================================

interface TourContextValue {
  /** Whether the product tour is currently running */
  isTourActive: boolean
  setTourActive: (active: boolean) => void

  /** Signal for the Navbar to hold the mobile drawer open */
  openNavDrawer: boolean
  setOpenNavDrawer: (open: boolean) => void

  /** Signal to show the demo property card on the properties page */
  showDemoProperty: boolean
  setShowDemoProperty: (show: boolean) => void
}

// =============================================================================
// Context
// =============================================================================

const TourContext = createContext<TourContextValue | null>(null)

// =============================================================================
// Provider
// =============================================================================

export function TourProvider({ children }: { children: ReactNode }) {
  const [isTourActive, setTourActive] = useState(false)
  const [showDemoProperty, setShowDemoProperty] = useState(false)
  const [openNavDrawer, setOpenNavDrawer] = useState(false)

  const value: TourContextValue = {
    isTourActive,
    setTourActive: useCallback((active: boolean) => setTourActive(active), []),
    showDemoProperty,
    setShowDemoProperty: useCallback((show: boolean) => setShowDemoProperty(show), []),
    openNavDrawer,
    setOpenNavDrawer: useCallback((open: boolean) => setOpenNavDrawer(open), []),
  }

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>
}

// =============================================================================
// Hook
// =============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useTourContext(): TourContextValue {
  const context = useContext(TourContext)
  if (!context) {
    // Return a no-op fallback when used outside of TourProvider (e.g. in tests)
    return {
      isTourActive: false,
      setTourActive: () => {},
      showDemoProperty: false,
      setShowDemoProperty: () => {},
      openNavDrawer: false,
      setOpenNavDrawer: () => {},
    }
  }
  return context
}
